import { ConcordiaNav } from '@maxpreps/apiserviceclients/apiModels';
import React, { useState } from 'react';
import Sticky from 'react-stickynode';
import styled from 'styled-components';
import useIsAdmin from '../../hooks/useIsAdmin';
import { useIsMobile, useIsScreenSize } from '../../hooks/useIsScreenSize';
import { COLOR } from '../../styles/colors';
import SkyboxAdUnit from '../AdUnit/SkyboxAdUnit';
import NetworkBar from '../NetworkBar/NetworkBar';
import SiteNav from '../SiteNav/SiteNav';
import { SITE_NAV_BREAK_POINT } from '../SiteNav/SiteNavUtils';

const StyledGlobalHeader = styled.div`
  background-color: ${COLOR.white};
`;

interface IStyledFallbackProps {
  $height: number;
}

const StyledFallback = styled.div<IStyledFallbackProps>`
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 100;

  body.skybox-loaded & {
    background-color: #fff;
    height: ${props => props.$height}px;
  }

  body:not(.skybox-loaded) & {
    height: 0;
  }
`;

interface ISubheaderConfig {
  key: string;
  component: any;
  zIndex?: number;
  sticky?: boolean;
  height?: number;
}

type StickyProperties =
  | {
      top: number;
      activeClass: string;
    }
  | { enabled: false };

type ICommonProperties = {
  key: string;
  innerZ: number;
};

const getStickyProps: (
  config: ISubheaderConfig,
  getStickyOffset: (height: number) => number,
  isMobile: boolean
) => ICommonProperties & StickyProperties = (
  config,
  getStickyOffset,
  isMobile
) => {
  const { sticky, height: _height, zIndex, key } = config;
  // This is a hack since we don't have access to isMobile in _app where height is defined
  const height = key === 'teamNav' && isMobile ? 44 : _height;

  const commonProps: ICommonProperties = {
    key,
    innerZ: zIndex ?? 100
  };

  const stickyProps: StickyProperties = sticky
    ? {
        top: getStickyOffset(height || 0),
        activeClass: 'stuck'
      }
    : {
        enabled: false
      };

  return {
    ...commonProps,
    ...stickyProps
  };
};

const getSubheaders = (
  subheaderData: ISubheaderConfig[],
  headerTopMargin: number,
  isMobile: boolean
) => {
  let stickyOffset = headerTopMargin;

  // this function will get the offset that THIS component should use
  // PLUS get the value ready for the following component by passing in this component's height
  const getStickyOffset = (componentHeight: number): number => {
    const _temp = stickyOffset;
    stickyOffset += componentHeight;

    return _temp;
  };

  return subheaderData.map(data => (
    // key is defined in ICommonProperties which is guarenteed to return
    // eslint-disable-next-line react/jsx-key
    <Sticky {...getStickyProps(data, getStickyOffset, isMobile)}>
      {data.component}
    </Sticky>
  ));
};

interface ISubheaderConfig {
  key: string;
  component: any;
  sticky?: boolean;
  height?: number;
  zIndex?: number;
}

const NETWORK_BAR_CONFIG: ISubheaderConfig = {
  sticky: false,
  component: <NetworkBar />,
  key: 'network_bar'
};

interface IGlobalHeaderWrapperProps {
  subheaders: ISubheaderConfig[];
  siteNavData: ConcordiaNav;
}

interface IGlobalHeaderProps extends IGlobalHeaderWrapperProps {
  isMobile: boolean;
  isMobileSkybox: boolean;
}

const GlobalHeader: React.FC<IGlobalHeaderProps> = ({
  subheaders = [],
  siteNavData,
  isMobileSkybox = true,
  isMobile = true
}) => {
  const [headerTopMargin, setHeaderTopMargin] = useState(0);
  const isAdminPage = useIsAdmin();

  let allSubheaders: ISubheaderConfig[] = [];
  if (!isAdminPage) {
    allSubheaders.push(NETWORK_BAR_CONFIG);
  }

  allSubheaders = [
    ...allSubheaders,
    {
      sticky: true,
      height: 52,
      zIndex: 110,
      component: <SiteNav siteNavData={siteNavData} />,
      key: 'site_nav'
    },
    ...subheaders
  ];

  return (
    <StyledGlobalHeader>
      <SkyboxAdUnit
        preferDesktop={!isMobileSkybox}
        setHeight={setHeaderTopMargin}
      />
      <StyledFallback $height={headerTopMargin} />
      {getSubheaders(allSubheaders, headerTopMargin, isMobile)}
    </StyledGlobalHeader>
  );
};

const MemoizedGlobalHeader = React.memo(GlobalHeader);

/*
  This component exists as a POC for optimizing re-renders with memoization.
  The react-responsive library internally uses an unmemoized useState hook,
  which in some cases causes unnecessary re-renders for consumers of the useMediaQuery hook and its derivatives.
  By wrapping GlobalHeader in React.memo and managing the responsive hooks in this wrapper,
  we are able to offset the expensive part of re-rendering because only the wrapper will be affected.
*/
const GlobalHeaderWrapper: React.FC<IGlobalHeaderWrapperProps> = props => {
  const isMobile = useIsMobile();
  // should be good once useIsScreenSize gets updated to ts
  const isMobileSkybox = !useIsScreenSize(SITE_NAV_BREAK_POINT as any);

  return (
    <MemoizedGlobalHeader
      {...props}
      isMobile={isMobile}
      isMobileSkybox={isMobileSkybox}
    />
  );
};

export default GlobalHeaderWrapper;
